/**
 * 【项目物料】-【项目商品池】页签（MPM v5.0.0，对应原型 SCP 项目商品池）
 *
 * 项目下拉单独一行（与【我提报的】一致，SearchFilter 不支持自定义组件），7 项固定查询条件、状态页签、
 * 商品卡片 / 表格、分页、详情抽屉均与 MPM 项目商品池一致（用户确认）；列按 SCP服务商 字段配置展示。
 */
import { Button, Empty, Pagination, Radio, Select, Space } from 'antd'
import { AppstoreOutlined, UnorderedListOutlined } from '@ant-design/icons'
import Search from '@/components/SearchFilter'
import type { formName } from '@/components/SearchFilter/formItem'
import Buttonbar from '@/components/ButtonBar'
import RadioTabs from '@/components/RadioTabs'
import PoolCards from './components/PoolCards'
import PoolTable from './components/PoolTable'
import DetailDrawer from './components/DetailDrawer'
import { PAGE_SIZE_OPTIONS, STATUS_TABS } from './reducer'
import useProjectPool from './hook'
import Style from './style.module.scss'

/** 无授权项目（PRD COM-ERR-001、SCP-TAB-001 AC-05） */
const NO_PROJECT_TEXT = '暂无项目物料查看权限，请联系项目经理配置。'

/** 状态页签字典（RadioTabs 格式：key → { value: 显示名 }） */
const STATUS_TAB_DICT = Object.fromEntries(
  STATUS_TABS.map((tab) => [tab.key, { value: tab.label }]),
)

/** 固定 7 项业务筛选 */
const SEARCH_FIELDS: formName[] = [
  'poolProductCode',
  'poolCustomerTitle',
  'poolBrandName',
  'poolCategoryName',
  'poolRemark',
  'poolStandardCode',
  'poolMaterialCode',
]

interface Props {
  restore: boolean // 是否从页面缓存恢复（从其他页面切回来时为真）
  active: boolean // 是否为当前页签（切回来时清空勾选）
}

const ProjectPool = (props: Props) => {
  const { restore, active } = props
  const {
    state,
    searchRef,
    fields,
    exportEnabled,
    search,
    changeProject,
    changeTab,
    changePage,
    refresh,
    changeViewMode,
    setSelectedIds,
    toggleSelected,
    exportPool,
    openDetail,
    closeDetail,
    restoreFocus,
  } = useProjectPool(restore, active)
  const {
    projects,
    projectsLoading,
    platformCode,
    statusTab,
    pageNo,
    pageSize,
    list,
    total,
    loading,
    viewMode,
    selectedIds,
    detailCode,
    exporting,
  } = state
  const noProject = !projectsLoading && !projects.length

  return (
    <div className={Style.container}>
      {/* 项目名称：必选上下文，不可清空 */}
      <div className={Style.projectSelector}>
        <span className={Style.projectLabel}>
          <span className={Style.required}>*</span>项目名称：
        </span>
        <Select
          className={Style.projectSelect}
          value={platformCode}
          onChange={changeProject}
          loading={projectsLoading}
          placeholder="请选择项目名称"
          showSearch
          optionFilterProp="label"
          options={projects.map((project) => ({
            label: project.name,
            value: project.code,
          }))}
        />
      </div>
      {noProject ? (
        <Empty className={Style.empty} description={NO_PROJECT_TEXT} />
      ) : (
        <>
          <Search
            keyName="PROJECTPOOL"
            ScreenKey="PROJECTPOOL"
            currentRef={searchRef}
            fieldsData={SEARCH_FIELDS}
            onSearch={search}
            hideScheme
          />
          <Buttonbar onRefresh={refresh}>
            <Space size={8}>
              {/* 当前项目允许导出时展示；提交中转圈并禁止重复点击 */}
              {exportEnabled && (
                <Button
                  type="primary"
                  loading={exporting}
                  disabled={loading}
                  onClick={exportPool}
                >
                  物料导出
                </Button>
              )}
              <Radio.Group
                size="small"
                buttonStyle="solid"
                value={viewMode}
                onChange={(e) => changeViewMode(e.target.value)}
              >
                <Radio.Button value="table" aria-label="表格列表">
                  <UnorderedListOutlined />
                </Radio.Button>
                <Radio.Button value="card" aria-label="商品卡片">
                  <AppstoreOutlined />
                </Radio.Button>
              </Radio.Group>
            </Space>
          </Buttonbar>
          {/* 状态页签：SCP 公共 RadioTabs 样式 */}
          <div className={Style.statusTabs}>
            <RadioTabs
              defaultKey={statusTab}
              radioItemDict={STATUS_TAB_DICT}
              onChange={changeTab}
            />
          </div>
          {/* 列表区：占满剩余高度，只在表格 / 卡片区域内滚动，分页贴底 */}
          <div className={Style.listArea}>
            {viewMode === 'card' ? (
              <>
                <PoolCards
                  list={list}
                  fields={fields}
                  loading={loading}
                  selectedIds={selectedIds}
                  onToggle={toggleSelected}
                  onSelect={setSelectedIds}
                  onOpenDetail={openDetail}
                />
                <div className={Style.pagination}>
                  <Pagination
                    size="small"
                    current={pageNo}
                    pageSize={pageSize}
                    total={total}
                    showSizeChanger
                    showQuickJumper
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    showTotal={(count) => `共 ${count} 条`}
                    // 切换每页条数时回到第 1 页（与表格一致）
                    onChange={(page, size) =>
                      changePage(size !== pageSize ? 1 : page, size)
                    }
                  />
                </div>
              </>
            ) : (
              <PoolTable
                // 公共 TableAntd 只在数据变化时读取勾选；切回本页签时重新挂载以清空勾选框
                key={String(active)}
                list={list}
                fields={fields}
                loading={loading}
                selectedIds={selectedIds}
                onSelect={setSelectedIds}
                pagination={{
                  total,
                  current: pageNo,
                  pagesize: pageSize,
                  onChange: changePage,
                }}
                onOpenDetail={openDetail}
              />
            )}
          </div>
        </>
      )}
      <DetailDrawer
        platformCode={platformCode}
        xhCode={detailCode}
        onClose={closeDetail}
        onAfterClose={restoreFocus}
      />
    </div>
  )
}

export default ProjectPool
/**
 * 【项目物料】-【项目商品池】页签（MPM v5.0.0，对应原型 SCP 项目商品池）
 *
 * 项目下拉单独一行（与【我提报的】一致，SearchFilter 不支持自定义组件），7 项固定查询条件、状态页签、
 * 商品卡片 / 表格、分页、详情抽屉均与 MPM 项目商品池一致（用户确认）；列按 SCP服务商 字段配置展示。
 */
import { Button, Empty, Pagination, Radio, Select, Space } from 'antd'
import { AppstoreOutlined, UnorderedListOutlined } from '@ant-design/icons'
import Search from '@/components/SearchFilter'
import type { formName } from '@/components/SearchFilter/formItem'
import Buttonbar from '@/components/ButtonBar'
import RadioTabs from '@/components/RadioTabs'
import PoolCards from './components/PoolCards'
import PoolTable from './components/PoolTable'
import DetailDrawer from './components/DetailDrawer'
import { PAGE_SIZE_OPTIONS, STATUS_TABS } from './reducer'
import useProjectPool from './hook'
import Style from './style.module.scss'

/** 无授权项目（PRD COM-ERR-001、SCP-TAB-001 AC-05） */
const NO_PROJECT_TEXT = '暂无项目物料查看权限，请联系项目经理配置。'

/** 状态页签字典（RadioTabs 格式：key → { value: 显示名 }） */
const STATUS_TAB_DICT = Object.fromEntries(
  STATUS_TABS.map((tab) => [tab.key, { value: tab.label }]),
)

/** 固定 7 项业务筛选 */
const SEARCH_FIELDS: formName[] = [
  'poolProductCode',
  'poolCustomerTitle',
  'poolBrandName',
  'poolCategoryName',
  'poolRemark',
  'poolStandardCode',
  'poolMaterialCode',
]

interface Props {
  restore: boolean // 是否从页面缓存恢复（从其他页面切回来时为真）
  active: boolean // 是否为当前页签（切回来时清空勾选）
}

const ProjectPool = (props: Props) => {
  const { restore, active } = props
  const {
    state,
    searchRef,
    fields,
    exportEnabled,
    search,
    changeProject,
    changeTab,
    changePage,
    refresh,
    changeViewMode,
    setSelectedIds,
    toggleSelected,
    exportPool,
    openDetail,
    closeDetail,
    restoreFocus,
  } = useProjectPool(restore, active)
  const {
    projects,
    projectsLoading,
    platformCode,
    statusTab,
    pageNo,
    pageSize,
    list,
    total,
    loading,
    viewMode,
    selectedIds,
    detailCode,
    exporting,
  } = state
  const noProject = !projectsLoading && !projects.length

  return (
    <div className={Style.container}>
      {/* 项目名称：必选上下文，不可清空 */}
      <div className={Style.projectSelector}>
        <span className={Style.projectLabel}>
          <span className={Style.required}>*</span>项目名称：
        </span>
        <Select
          className={Style.projectSelect}
          value={platformCode}
          onChange={changeProject}
          loading={projectsLoading}
          placeholder="请选择项目名称"
          showSearch
          optionFilterProp="label"
          options={projects.map((project) => ({
            label: project.name,
            value: project.code,
          }))}
        />
      </div>
      {noProject ? (
        <Empty className={Style.empty} description={NO_PROJECT_TEXT} />
      ) : (
        <>
          <Search
            keyName="PROJECTPOOL"
            ScreenKey="PROJECTPOOL"
            currentRef={searchRef}
            fieldsData={SEARCH_FIELDS}
            onSearch={search}
            hideScheme
          />
          <Buttonbar onRefresh={refresh}>
            <Space size={8}>
              {/* 当前项目允许导出时展示；提交中转圈并禁止重复点击 */}
              {exportEnabled && (
                <Button
                  type="primary"
                  loading={exporting}
                  disabled={loading}
                  onClick={exportPool}
                >
                  物料导出
                </Button>
              )}
              <Radio.Group
                size="small"
                buttonStyle="solid"
                value={viewMode}
                onChange={(e) => changeViewMode(e.target.value)}
              >
                <Radio.Button value="table" aria-label="表格列表">
                  <UnorderedListOutlined />
                </Radio.Button>
                <Radio.Button value="card" aria-label="商品卡片">
                  <AppstoreOutlined />
                </Radio.Button>
              </Radio.Group>
            </Space>
          </Buttonbar>
          {/* 状态页签：SCP 公共 RadioTabs 样式 */}
          <div className={Style.statusTabs}>
            <RadioTabs
              defaultKey={statusTab}
              radioItemDict={STATUS_TAB_DICT}
              onChange={changeTab}
            />
          </div>
          {/* 列表区：占满剩余高度，只在表格 / 卡片区域内滚动，分页贴底 */}
          <div className={Style.listArea}>
            {viewMode === 'card' ? (
              <>
                <PoolCards
                  list={list}
                  fields={fields}
                  loading={loading}
                  selectedIds={selectedIds}
                  onToggle={toggleSelected}
                  onSelect={setSelectedIds}
                  onOpenDetail={openDetail}
                />
                <div className={Style.pagination}>
                  <Pagination
                    size="small"
                    current={pageNo}
                    pageSize={pageSize}
                    total={total}
                    showSizeChanger
                    showQuickJumper
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    showTotal={(count) => `共 ${count} 条`}
                    // 切换每页条数时回到第 1 页（与表格一致）
                    onChange={(page, size) =>
                      changePage(size !== pageSize ? 1 : page, size)
                    }
                  />
                </div>
              </>
            ) : (
              <PoolTable
                // 公共 TableAntd 只在数据变化时读取勾选；切回本页签时重新挂载以清空勾选框
                key={String(active)}
                list={list}
                fields={fields}
                loading={loading}
                selectedIds={selectedIds}
                onSelect={setSelectedIds}
                pagination={{
                  total,
                  current: pageNo,
                  pagesize: pageSize,
                  onChange: changePage,
                }}
                onOpenDetail={openDetail}
              />
            )}
          </div>
        </>
      )}
      <DetailDrawer
        platformCode={platformCode}
        xhCode={detailCode}
        onClose={closeDetail}
        onAfterClose={restoreFocus}
      />
    </div>
  )
}

export default ProjectPool
